import { useSelector } from "react-redux";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import ChunkUploadFile from "components/ChunkUploadFile";
import ConfirmationModal from "components/ConfirmationModal";
import Modal from "components/Modal";
import { ExclamationSVGIcon } from "components/SVGIcons";
import { UPLOAD_DOCUMENT_URLS } from "constants/routes";
import { IFRAttachment, InformationFormData, IUpdatedFRAttachment } from "models/HaulPay";
import { selectUserDetails } from "store/user/selector";
import styles from "./style.module.scss";
import { deleteFRAttachmentAPI } from "services/api/haulpay";
import { showErrorMessage } from "helpers/toastMessage";
import { useCallback, useMemo, useState } from "react";
import AttachmentItem from "components/AttachmentItem";

interface SharedValue {
  informationFormData?: InformationFormData;
  uploadedFiles: IFRAttachment[];
  createdFR: any;
  handleNext: VoidFunction;
  handleBack: VoidFunction;
  setUploadedFiles: (files: IFRAttachment[]) => void;
}

export default function PayDocuments({
  informationFormData,
  createdFR,
  handleNext,
  handleBack,
  uploadedFiles,
  setUploadedFiles
}: Readonly<SharedValue>) {
  const userDetails = useSelector(selectUserDetails);
  const [isConfirmModalOpen, toggleConfirmModal] = useToggle();
  const [isRateConfirmModalOpen, toggleRateConfirmModal] = useToggle();
  const [isBOLRequiredModalOpen, toggleBOLRequiredModal] = useToggle();
  const [updatingFile, setUpdatingFile] = useState<IFRAttachment[]>([]);

  const goToConfirmation = useCallback(() => {
    const hasRateConfirmation = uploadedFiles.find((file) =>
      file.category.includes("Rate Confirmation")
    );
    const hasSignedBill = uploadedFiles.find(
      (file) =>
        file.category.includes("Signed Bill of Lading (Delivered)") ||
        file.category.includes("Signed Bill of Lading (Pick-Up)")
    );
    const hasTruckOrderUsed = uploadedFiles.find((file) =>
      file.category.includes("Truck Order Not Used")
    );

    if (!uploadedFiles.length) {
      toggleConfirmModal();
    } else if (informationFormData?.isFuelAdvance && !hasSignedBill) {
      toggleBOLRequiredModal();
    } else if ((hasRateConfirmation && hasSignedBill) || hasTruckOrderUsed) {
      handleNext();
    } else {
      toggleRateConfirmModal();
    }
  }, [uploadedFiles]);

  const resetData = () => {
    toggleConfirmModal();
    setUploadedFiles([]);
    handleBack();
  };

  const deleteFile = async (fileId: string) => {
    if (createdFR.id) {
      try {
        await deleteFRAttachmentAPI(createdFR.id, fileId);
        setUploadedFiles(uploadedFiles.filter((item: IFRAttachment) => item.id !== fileId));
      } catch {
        showErrorMessage("Failed to delete attachment.");
      }
    }
  };

  const updatedFileId = useMemo(() => {
    if (updatingFile.length > 0) return updatingFile[0].id;
  }, [updatingFile]);

  return (
    <div className={styles.payDocumentsContainer}>
      <div className={styles.body}>
        <h4 className={styles.header}>Documents</h4>
        <div className={styles.uploadWrapper}>
          <ChunkUploadFile
            className="desktop:flex desktop:flex-col desktop:gap-4 desktop:text-lg "
            multiple
            urlCallback={
              UPLOAD_DOCUMENT_URLS({
                factoring_id: userDetails.factoring_id,
                funding_request_id: createdFR.id
              }).FR_ATTACHMENT
            }
            onUploadFinished={(files: any, fileId: string) => {
              const newFiles: IFRAttachment[] = files.map((file: any) => ({
                fileId: fileId,
                id: file.id,
                filename: file.name,
                category: file.categories.split(","),
                downloadUrl: file.preview_url,
                visibleTo: file.visible_to,
                type: file.fileType,
                size: file.fileSize
              }));
              setUploadedFiles([
                ...uploadedFiles.filter((item: IFRAttachment) => item.id !== updatedFileId),
                ...newFiles
              ]);
            }}
            updatedFile={
              updatingFile.map((file) => ({
                ...file,
                frId: createdFR.id
              })) as IUpdatedFRAttachment[]
            }
            setFile={setUpdatingFile}
          />
          {uploadedFiles.map((file) => (
            <div key={file.id} className="-mx-2">
              <AttachmentItem file={file} deleteFile={deleteFile} setFile={setUpdatingFile} />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="outline" onClick={toggleConfirmModal}>
          Complete Later
        </Button>
        <Button onClick={goToConfirmation}>Next</Button>
      </div>

      <Modal
        isOpen={isConfirmModalOpen}
        closeModal={toggleConfirmModal}
        wrapperClass="!my-auto desktop:!min-h-fit desktop:!w-1/2 text-sm"
        dialogClass="!bg-foreground !px-3 !py-4 gap-2.5"
        closeClass="!top-24 !right-6 !bg-transparent !text-text">
        <div className="flex items-center gap-1 text-xl font-bold text-primary">
          <ExclamationSVGIcon />
          <span>Confirm</span>
        </div>
        <p className="pt-1.5 font-medium">
          Are you sure you want to create an Incomplete Funding Request? You can navigate to
          Incomplete in Manage Pay to add documents later if you do.
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            rounded
            className="!w-20 text-sm !h-9 self-end"
            onClick={toggleConfirmModal}>
            No
          </Button>
          <Button rounded className="!w-20 text-sm !h-9 self-end" onClick={resetData}>
            Yes
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={isRateConfirmModalOpen}
        closeModal={toggleRateConfirmModal}
        wrapperClass="!my-auto desktop:!min-h-fit desktop:!w-1/2 text-sm"
        dialogClass="!bg-foreground !px-3 !py-4 gap-2.5"
        closeClass="!top-24 !right-6 !bg-transparent !text-text">
        <div className="flex items-center gap-1 text-xl font-bold text-primary">
          <ExclamationSVGIcon />
          <span>Confirm</span>
        </div>
        <p className="pt-1.5 font-medium">
          We require an uploaded BOL (Delivered) or BOL (Pick-Up) and Rate Confirmation unless Truck
          Order Not Used.
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            rounded
            className="!w-20 text-sm !h-9 self-end"
            onClick={toggleRateConfirmModal}>
            OK
          </Button>
        </div>
      </Modal>

      <ConfirmationModal
        content={
          "Since a fuel advance request has been submitted, a BOL (Bill of Lading) pick-up document is required."
        }
        isHideCancelButton
        isConfirmModalOpen={isBOLRequiredModalOpen}
        toggleConfirmModal={toggleBOLRequiredModal}
      />
    </div>
  );
}
